/**
 * ArgusSecure — marketing content (products, solutions, industries).
 * One source of truth for the mega-menu + dynamic pages.
 */

export const PRODUCTS = {
  sast: {
    slug: "sast",
    name: "Source Code Analysis",
    short: "SAST",
    tagline: "Find the flaw before the commit ships.",
    summary:
      "Static analysis that reads your code the way a senior engineer reads a pull request — fast, focused, and zero patience for hand-waving.",
    bullets: [
      "15 starter rules across Python, JavaScript, TypeScript.",
      "Eval, pickle, weak crypto, hardcoded secrets, SQL string concat — caught.",
      "Three-line context per finding with a one-line fix.",
      "CWE + OWASP Top 10 2021 mapping on every issue.",
    ],
    cta: "Scan My Code Now",
  },
  dast: {
    slug: "dast",
    name: "Web App Probing",
    short: "DAST",
    tagline: "Probe your app the way an attacker would.",
    summary:
      "OWASP-class dynamic analysis. Crawl, test, and validate vulnerabilities across your entire web attack surface — built on the same OWASP testing taxonomy ZAP and Burp use, with the raw request and response shown beside every finding.",
    bullets: [
      "OWASP Top 10 coverage with safe, conservative payloads.",
      "Domain verification gate — never scans without proof of ownership.",
      "SSRF, private-IP, and metadata-endpoint blocks at the scanner layer.",
      "Token-bucket rate limits to keep your origin honest.",
      "OWASP ZAP engine option (preview) — bring your own ZAP daemon for enterprise scans.",
    ],
    cta: "Probe My App",
  },
  sca: {
    slug: "sca",
    name: "Dependency Risk",
    short: "SCA",
    tagline: "Every dependency. Every CVE. Every minute.",
    summary:
      "Software Composition Analysis against the OSV database with an offline seed of well-known CVEs. Built for the npm and PyPI worlds you actually live in.",
    bullets: [
      "Parses package.json, requirements.txt, pyproject.toml, go.mod, pom.xml.",
      "24-hour CVE cache. Offline fallback ships with the platform.",
      "Fix-version recommendation on every match.",
      "Log4Shell, lodash, minimist, jsonwebtoken — all the usuals.",
    ],
    cta: "Check My Dependencies",
  },
  asm: {
    slug: "asm",
    name: "Attack Surface Inventory",
    short: "ASM",
    tagline: "You can't fix what you can't see.",
    summary:
      "An always-current inventory of your apps, repos, and dependencies. Risk score per asset. Trends by week.",
    bullets: [
      "One asset list across SAST, DAST, and SCA.",
      "Risk score: min(100, C×10 + H×5 + M×2 + L×0.5).",
      "Top-5 risky assets surfaced on the dashboard.",
      "Tag, group, and assign owners.",
    ],
    cta: "Show Me My Assets",
  },
  compliance: {
    slug: "compliance",
    name: "Compliance Evidence",
    short: "Compliance",
    tagline: "The evidence your auditor already trusts.",
    summary:
      "PDF reports built for boards, regulators, and the people writing your SOC 2. Executive summary + every finding + raw evidence.",
    bullets: [
      "One-click PDF per project.",
      "OWASP, CWE, CVSS on every finding.",
      "Audit log for every privileged action.",
      "Evidence encrypted at rest, redacted by default, reveal-on-click.",
    ],
    cta: "Download My Report",
  },
  remediation: {
    slug: "remediation",
    name: "Guided Remediation",
    short: "Fix It.",
    tagline: "A fix beside every flaw.",
    summary:
      "We don't just point at the problem. Every finding carries a plain-English fix, before/after code where the rule has one, and links to the right cheat sheet.",
    bullets: [
      "Step-by-step remediation per rule.",
      "Before / after code diffs.",
      "Direct links to OWASP and MITRE references.",
      "False-positive workflow with required reason.",
    ],
    cta: "See How",
  },
};

export const SOLUTIONS = {
  "application-security": {
    slug: "application-security",
    name: "Application Security",
    tagline: "Cover the full stack — code, app, dependencies — with one tool.",
    summary:
      "Most AppSec programmes are duct tape stretched across three vendors. ArgusSecure folds SAST, DAST, and SCA into one workflow with one source of truth.",
    bullets: [
      "One backlog of findings across three scanners.",
      "Deduped by fingerprint across scans.",
      "Risk score that survives noisy weeks.",
    ],
    related: ["sast", "dast", "sca"],
  },
  "devsecops": {
    slug: "devsecops",
    name: "DevSecOps",
    tagline: "Find it on the branch, not in production.",
    summary:
      "API-first by design. Drop ArgusSecure into your CI, post findings as PR comments, fail the build when criticals appear.",
    bullets: [
      "API tokens scoped per repo.",
      "Webhooks for new Critical and High.",
      "PR-comment posting (Round 5).",
    ],
    related: ["sast", "sca", "remediation"],
  },
  "compliance": {
    slug: "compliance",
    name: "Compliance & Audit",
    tagline: "Evidence on demand, never assembled the night before.",
    summary:
      "Every finding carries timestamped evidence. Every privileged action lands in the audit log. PDF reports built for the board.",
    bullets: [
      "AES-256-GCM envelope encryption on evidence.",
      "Audit log of scan start, status changes, evidence reveals.",
      "PDF executive summary + full findings.",
    ],
    related: ["compliance", "asm"],
  },
  "cloud-security": {
    slug: "cloud-security",
    name: "Cloud & Container",
    tagline: "Catch the misconfig before the metadata endpoint does.",
    summary:
      "Scan your container manifests and Infrastructure-as-Code for known CVEs and risky defaults. Cloud-metadata endpoints are blocked from being scanned, period.",
    bullets: [
      "SCA against container base images (Round 2).",
      "Cloud-metadata endpoint hard-block.",
      "Private IP refusal at scanner layer.",
    ],
    related: ["sca", "dast"],
  },
  "exposure-management": {
    slug: "exposure-management",
    name: "Exposure Management",
    tagline: "See every exposure. Fix what matters first.",
    summary:
      "Risk score per asset, trend per severity, top-five risky assets surfaced on a single pane. Stop hand-prioritising in spreadsheets.",
    bullets: [
      "30-day trend chart on the dashboard.",
      "Top-5 risky assets, ranked by exploitability.",
      "MTTR gauge across the org.",
    ],
    related: ["asm", "remediation"],
  },
  "ai-security": {
    slug: "ai-security",
    name: "AI / LLM Security",
    tagline: "Your model is only as safe as the dependencies behind it.",
    summary:
      "Scan the Python and JS stacks behind your inference pipeline. Catch insecure deserialization, weak crypto, and dependency CVEs in your model-serving code.",
    bullets: [
      "Python AST rules tuned for ML pipelines.",
      "Pickle/yaml.load detection — common in model serialisation.",
      "Dependency CVE matching for transformers, langchain, openai stacks.",
    ],
    related: ["sast", "sca"],
  },
};

export const INDUSTRIES = {
  "financial-services": {
    slug: "financial-services",
    name: "Financial Services",
    tagline: "The auditors trust evidence. So do we.",
    summary:
      "Tamper-evident audit log, encrypted evidence, redacted-by-default secret display, PDF reports built for examiners.",
    proof: "Designed for PCI DSS, FFIEC, and SOC 2 evidence requirements.",
  },
  healthcare: {
    slug: "healthcare",
    name: "Healthcare",
    tagline: "Patient data deserves more than checkbox security.",
    summary:
      "HIPAA-aware redaction defaults and an audit log of every privileged data access. The platform never persists scan responses unredacted.",
    proof: "HIPAA / HITRUST evidence patterns built in.",
  },
  technology: {
    slug: "technology",
    name: "Technology & SaaS",
    tagline: "Ship fast. Sleep well.",
    summary:
      "API-first. CI-friendly. Pricing that doesn't penalise a 12-person team that ships ten times a day.",
    proof: "Free tier covers your first real project. Pro is $49/mo.",
  },
  "public-sector": {
    slug: "public-sector",
    name: "Public Sector",
    tagline: "FedRAMP-track architecture. Built to be auditable.",
    summary:
      "On-prem option for Enterprise. NIST 800-53 evidence mapping. Hard-block of cloud-metadata endpoints by default.",
    proof: "On-prem-ready. Enterprise SSO + SAML on the roadmap.",
  },
  ecommerce: {
    slug: "ecommerce",
    name: "E-Commerce & Retail",
    tagline: "Black Friday isn't the day to discover Log4Shell.",
    summary:
      "Continuous SCA against your npm and PyPI surface. DAST probes for the OWASP Top 10 that actually steal carts.",
    proof: "Scan profiles tuned for checkout flows (Round 2).",
  },
  education: {
    slug: "education",
    name: "Education",
    tagline: "Student data first. Always.",
    summary:
      "FERPA-aware defaults, evidence retention controls, and pricing that respects an academic budget.",
    proof: "Education pricing on request via Talk to Security.",
  },
};

export const STATS = [
  { value: "100%", label: "Open evidence — never a black box" },
  { value: "<90s", label: "First finding after signup" },
  { value: "OWASP", label: "Top 10 2021 mapped on every finding" },
  { value: "AES-256", label: "GCM envelope encryption on evidence" },
];

export const ANALYST_QUOTES = [];
// Intentionally empty. Analyst quotes will only appear here when real, attributable
// analyst coverage exists. Inventing analyst-firm names is trademark infringement
// and a likely FTC false-advertising violation.

export const INTEGRATIONS = {
  live: [
    { name: "GitHub", note: "Manifest upload + repo URL." },
  ],
  coming: [
    { name: "GitHub App", note: "PR checks. Round 5 (within 90 days)." },
    { name: "Slack", note: "Alerts on Critical/High. Round 5 (within 90 days)." },
    { name: "Jira", note: "Ticket creation. Round 5 (within 90 days)." },
  ],
};

export const TRUST_BADGES = [
  "SOC 2 Type II (on track)",
  "ISO 27001 (on track)",
  "GDPR & CCPA aligned",
  "HIPAA-aware defaults",
];

export const CUSTOMER_STORIES = [];
// Intentionally empty. Real customer stories appear here only with written permission
// from the customer. Fabricated testimonials violate the FTC Endorsement Guides
// (16 CFR Part 255). Design partners onboarding now.
